import { isDev } from './config.js';

const pendingDebugConsoleList = [];
let flushDebugConsoleTimer = null;

/**
 * 功能说明：获取当前运行环境可用的控制台对象。
 * @returns {Console|null} 支持控制台输出时返回 console 对象，否则返回 null
 */
function getDebugConsole() {
  // 浏览器环境优先使用 window.console，便于 H5 页面调试。
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  // 非浏览器环境兜底读取 globalThis.console。
  if (typeof globalThis !== 'undefined' && globalThis.console) {
    return globalThis.console;
  }
  return null;
}

/**
 * 功能说明：判断当前环境是否可以输出分组调试日志。
 * @param {Console|null} debugConsole - 控制台对象
 * @returns {boolean} 支持分组调试输出时返回 true
 */
function canPrintDebugConsole(debugConsole) {
  return (
    isDev &&
    debugConsole &&
    typeof debugConsole.group === 'function' &&
    typeof debugConsole.groupEnd === 'function' &&
    typeof debugConsole.log === 'function'
  );
}

/**
 * 功能说明：按指定字段对日志列表分组。
 * @param {Array<Object>} list - 调试日志列表
 * @param {string} key - 分组字段名
 * @param {string} fallback - 分组字段为空时的兜底名称
 * @returns {Object} 分组后的日志映射
 */
function groupDebugConsoleList(list, key, fallback) {
  return list.reduce((map, item) => {
    const groupName = item && item[key] ? item[key] : fallback;
    // 首次遇到分组时创建列表容器。
    if (!map[groupName]) {
      map[groupName] = [];
    }
    map[groupName].push(item);
    return map;
  }, {});
}

/**
 * 功能说明：输出单条调试日志内容。
 * @param {Console} debugConsole - 控制台对象
 * @param {Object} item - 单条调试日志
 * @returns {void} 无返回值
 */
function printDebugConsoleItem(debugConsole, item) {
  const title = item && item.title ? `[${item.title}]` : '[debug]';
  // 未传入调试数据时，仅输出当前阶段标题。
  if (!item || item.data === undefined) {
    debugConsole.log(title);
    return;
  }
  debugConsole.log(title, item.data);
}

/**
 * 功能说明：输出一批按页面和方法聚合后的调试日志。
 * @param {Console} debugConsole - 控制台对象
 * @param {Array<Object>} list - 待输出日志列表
 * @returns {void} 无返回值
 */
function printDebugConsoleBatch(debugConsole, list) {
  const pageMap = groupDebugConsoleList(list, 'page', 'debug');
  Object.keys(pageMap).forEach((page) => {
    debugConsole.group(`[${page}]`);
    try {
      const methodMap = groupDebugConsoleList(pageMap[page], 'method', 'debug');
      Object.keys(methodMap).forEach((method) => {
        debugConsole.group(`[${method}]`);
        try {
          methodMap[method].forEach((item) => {
            printDebugConsoleItem(debugConsole, item);
          });
        } finally {
          debugConsole.groupEnd();
        }
      });
    } finally {
      debugConsole.groupEnd();
    }
  });
}

/**
 * 功能说明：刷新并输出当前等待中的调试日志。
 * @returns {void} 无返回值
 */
function flushDebugGroupConsole() {
  flushDebugConsoleTimer = null;
  const debugConsole = getDebugConsole();
  // 当前环境不支持控制台分组时，清空队列并跳过打印。
  if (!canPrintDebugConsole(debugConsole)) {
    pendingDebugConsoleList.splice(0);
    return;
  }
  const list = pendingDebugConsoleList.splice(0);
  // 没有待输出日志时直接结束。
  if (!list.length) {
    return;
  }
  printDebugConsoleBatch(debugConsole, list);
}

/**
 * 功能说明：安排下一轮统一输出调试日志。
 * @returns {void} 无返回值
 */
function scheduleFlushDebugGroupConsole() {
  // 已存在刷新任务时复用当前任务，避免同一批日志重复调度。
  if (flushDebugConsoleTimer) {
    return;
  }
  flushDebugConsoleTimer = setTimeout(flushDebugGroupConsole, 0);
}

/**
 * 功能说明：按页面、方法和标题输出默认展开的开发环境调试日志。
 * @param {Object} options - 日志参数
 * @param {string} options.page - 页面或模块名称
 * @param {string} options.method - 方法名称
 * @param {string} options.title - 当前日志阶段标题
 * @param {*} [options.data] - 需要输出的调试数据
 * @returns {void} 无返回值
 */
export function debugGroupConsole({ page, method, title, data } = {}) {
  try {
    const debugConsole = getDebugConsole();
    // 当前环境不支持控制台分组时，按需求直接跳过打印。
    if (!canPrintDebugConsole(debugConsole)) {
      return;
    }

    pendingDebugConsoleList.push({
      page,
      method,
      title,
      data
    });
    scheduleFlushDebugGroupConsole();
  } catch (error) {
    console.error(error);
    // 处理错误
  }
}
